<script lang="ts">
  import type { ChartConfiguration } from "chart.js"
  import Card from "@kontextwerk/web-sdk/ui/layout/Card.svelte"
  import ChartCanvas from "@kontextwerk/web-sdk/ui/charts/components/ChartCanvas.svelte"
  import { stackedUsageBar } from "@kontextwerk/web-sdk/core/charts/presets/bar"
  import { resolveChartTheme } from "@kontextwerk/web-sdk/core/charts/theme/resolveTheme"
  import { createExternalTooltipHandler } from "@kontextwerk/web-sdk/core/charts/plugins/externalTooltip"
  import { kwHoverHighlight } from "@kontextwerk/web-sdk/core/charts/plugins/hoverHighlight"
  import type { HomeDashboardCommunicationPoint } from "../../../../../../../../../../../../api/kehrblatt/home-dashboard"
  import { formatDashboardDay } from "../../../../../../../../_shared/_logic/home-dashboard"

  const { points }: { points: HomeDashboardCommunicationPoint[] } = $props()

  const ChartCanvasView: any = ChartCanvas

  let chartScope = $state<HTMLElement | null>(null)
  const theme = $derived.by(() => resolveChartTheme(chartScope, { variant: "light" }))
  const axisLabelColor = $derived.by(() => resolveChartTheme(chartScope, { variant: "dark" }).text)
  const labels = $derived(points.map((point) => formatDashboardDay(point.date)))
  const outboundValues = $derived(points.map((point) => Number(point.outbound ?? 0)))
  const inboundValues = $derived(points.map((point) => Number(point.inbound ?? 0)))
  const hasData = $derived(
    outboundValues.some((value) => value > 0) || inboundValues.some((value) => value > 0),
  )
  const tooltipHandler = createExternalTooltipHandler({
    getTitle: ({ dataIndex }) => labels[dataIndex] ?? "",
    getRows: ({ dataIndex }) => [
      { label: "Ausgehend", value: Number(outboundValues[dataIndex] ?? 0).toLocaleString("de-DE") },
      {
        label: "Rückmeldungen",
        value: Number(inboundValues[dataIndex] ?? 0).toLocaleString("de-DE"),
      },
    ],
  })
  const hoverHighlightPlugin = kwHoverHighlight({ fill: "rgba(0, 0, 0, 0.04)", paddingPx: 8 })
  const chartConfig = $derived.by((): ChartConfiguration<"bar"> | null => {
    const chartTheme = { ...theme, text: axisLabelColor }
    const config = stackedUsageBar({
      labels,
      series: [
        { label: "Ausgehend", values: outboundValues, color: "#005282" },
        { label: "Rückmeldungen", values: inboundValues, color: "#00ce5d" },
      ],
      theme: chartTheme,
      externalTooltip: tooltipHandler,
      plugins: [hoverHighlightPlugin],
    })

    ;(config as any).data = {
      ...(config as any).data,
      datasets: ((config as any).data?.datasets ?? []).map((dataset: any) => ({
        ...dataset,
        borderRadius: 0,
      })),
    }

    return config as ChartConfiguration<"bar">
  })
</script>

<div class="chart-card" bind:this={chartScope}>
  <Card>
    {#snippet contentSnippet()}
      <div class="chart-card__header">
        <h4>Terminvereinbarungen</h4>
        <small>Diese Woche</small>
      </div>
      {#if hasData}
        <div class="chart-card__canvas">
          <ChartCanvasView
            config={chartConfig}
            height={240}
            ariaLabel="Terminvereinbarungen diese Woche"
          />
        </div>
      {:else}
        <div class="chart-empty"
          >Noch keine ausgehenden Anfragen oder Rückmeldungen in dieser Woche.</div
        >
      {/if}
    {/snippet}
  </Card>
</div>

<style lang="less">
  .chart-card {
    display: flex;
    flex-direction: column;
  }

  .chart-card__header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.2rem;
    margin: 0.2rem 0 0.85rem;
    text-align: center;
  }

  .chart-card__header h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 800;
    color: var(--text-100);
  }

  .chart-card__header small {
    letter-spacing: 0;
    color: color-mix(in srgb, var(--text-100) 78%, transparent);
    font-size: 10px;
    font-weight: 700;
  }

  .chart-card__canvas {
    position: relative;
    height: 240px;
  }

  .chart-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 240px;
    color: color-mix(in srgb, var(--text-100) 70%, transparent);
    text-align: center;
    line-height: 1.4;
  }
</style>
